import { APPLICATION_STATUS } from "../configs/constants/ApplicationStatus";
import { JOB_STATUS } from "../configs/constants/JobStatus";
import Address from "./Address";
import { CustomFieldValues, ResolvedCustomFields } from "./Organisation";
import Competence from "./Competence";
import CustomerUser from "./CustomerUser";
import { ApplicationMetadata, JobMetadata } from "./Metadata";
import { Absences, CertificationsByType, ConsultantCertification } from "./Consultant";
import { AbsenceType } from "../configs/constants/AbsencesType";

/**
 * The MATCHING GATE of a job — consultant certifications/tags a job REQUIRES
 * (AND semantics). References the org's `certificationTypes` /
 * `consultantTagTypes` taxonomies. NOT to be confused with `job.tags`, which
 * are descriptive labels from the separate `jobTagTypes` taxonomy.
 */
export interface JobRequirements {
	certifications: { typeKey: string }[];
	tags: { key: string }[];
}

export default interface Job {
	id: string;
	orderNumber: string;
	customerOrderNumber?: string;
	organisation: { id: string };
	customer: {
		id: string;
		name?: string;
		breakTime?: number;
		/** The customer's tags (org `customerTagTypes` keys); only present when the customer is expanded. */
		tags?: { key: string }[];
	};
	contract: { id: string; name?: string };
	startDate: Date | string;
	endDate: Date | string;
	metadata: JobMetadata;
	description: string;
	contactPersons: CustomerUser[];
	numPositions: number;
	status: JOB_STATUS;
	competence: Competence;
	bookingType: BookingTypes;
	price: {
		hourlyPrice: number;
		totalPrice: number;
		vat: number;
		taxDeduction?: {
			[key in TaxTypes]?: {
				value: number;
				percentage: number;
			};
		};
		tg?: number;
	};
	applicationCount?: number;
	address?: Address;
	applications?: Application[];
	autoBooking?: boolean;
	customerDescription?: string;
	hidden?: boolean;
	history?: History[];
	images?: FileData[];
	documents?: FileData[];
	internalNotes?: string;
	invitations?: Invitation[];
	noOfHours?: number;
	pool?: string;
	purchaseOrderNo?: string;
	invoiceReference?: string;
	gflRate?: number;
	shifts?: JobShift[];
	title?: string;
	videos?: string[];
	isLongTerm?: boolean;
	parent?: {
		id: string;
	};
	flag?: boolean;
	/** Matching gate — required consultant certifications/tags (see {@link JobRequirements}). */
	requirements?: JobRequirements;
	/** DESCRIPTIVE labels; each key references org `jobTagTypes[].key`. Not a matching gate. */
	tags?: { key: string }[];
	/** The job's OWN custom field values (job fields + overrides of inheritable customer fields). */
	customFields?: CustomFieldValues;
	/**
	 * Effective values — own overrides over the customer's inheritable fields.
	 * Only on the single-job GET, never on the list.
	 */
	resolvedCustomFields?: ResolvedCustomFields;
}

export interface CreateJobRequest {
	address?: Address;
	autoBooking?: boolean;
	competence: Competence;
	contactPersons: CustomerUser[];
	contract?: { id: string };
	customer: { id: string };
	customerDescription?: string;
	description: string;
	hidden?: boolean;
	images?: FileData[];
	documents?: FileData[];
	internalNotes?: string;
	invitations?: string[];
	numPositions: number;
	orderNumber?: string;
	customerOrderNumber?: string;
	organisation?: {
		id: string;
	};
	purchaseOrderNo?: string;
	invoiceReference?: string;
	gflRate?: number;
	shifts: JobShift[];
	title?: string;
	videos?: string[];
	bookingType: BookingTypes;
	parent?: {
		id: string;
	};
	/**
	 * Matching gate — the admin-edited final requirement list, snapshotted at
	 * create time. When omitted the backend derives a suggested set from the
	 * org's `competenceRequirements`.
	 */
	requirements?: JobRequirements;
	/** DESCRIPTIVE labels; each key references org `jobTagTypes[].key`. Not a matching gate. */
	tags?: { key: string }[];
	/** Org-defined custom field values, keyed by `customFieldDefinitions[].key` (entity "job"). */
	customFields?: CustomFieldValues;
}

export interface UpdateJobRequest {
	address?: Address;
	autoBooking?: boolean;
	competence?: Competence;
	contactPersons?: CustomerUser[];
	customerDescription?: string;
	description?: string;
	hidden?: boolean;
	images?: FileData[];
	documents?: FileData[];
	internalNotes?: string;
	numPositions?: number;
	purchaseOrderNo?: string;
	invoiceReference?: string;
	customerOrderNumber?: string;
	gflRate?: number;
	title?: string;
	videos?: string[];
	shifts?: JobShift[];
	customer?: { id: string };
	/** Matching gate — required consultant certifications/tags (see {@link JobRequirements}). */
	requirements?: JobRequirements;
	/** DESCRIPTIVE labels; each key references org `jobTagTypes[].key`. Not a matching gate. */
	tags?: { key: string }[];
	/** Org-defined custom field values, keyed by `customFieldDefinitions[].key` (entity "job"). */
	customFields?: CustomFieldValues;
}

export type DateOrString = string | Date;

type ShiftStatus = "EXTEND_REQUESTED" | "EXTEND_APPROVED" | "EXTEND_DECLINED";

// TODO: What is difference between JobShift and Shift?
export interface JobShift {
	id?: string;
	end: DateOrString;
	start: DateOrString;
	status?: ShiftStatus;
	applications?: Application[];
	invitations?: Invitation[];
	break?: number;
	/** Planner row index for parallel unassigned shifts (presentation-only). */
	lane?: number;
}

export interface Shift {
	id: string;
	end: DateOrString;
	start: DateOrString;
	hasWorked: boolean;
	job: {
		id: string;
	};
	status?: ShiftStatus;
	applications?: Application[];
	invitations?: Invitation[];
	break?: number;
	/** Planner row index for parallel unassigned shifts (presentation-only). */
	lane?: number;
}

interface History {
	args?: any;
	date?: string;
	type?: JOB_STATUS;
	user?: {
		id?: string;
	};
}
export interface ContactPersons {
	email?: string;
	firstName?: string;
	id?: string;
	lastName?: string;
	phone?: string;
	profile?: { profilePicture: string };
}

export interface Application {
	consultant: Consultant;
	orderNumber: string;
	customer: {
		name: string;
	};
	id: string;
	status: APPLICATION_STATUS;
	metadata?: ApplicationMetadata;
	tb: number;
	tg: number;
	shifts: Shift[];
	// shifts?: { id: string }[];
	/** Immutable apply-time snapshot of the shifts (ids + times) the consultant
	 * applied for — kept server-side for backtracking, never updated. The actual
	 * booking is allocated at accept time (see wouldBookShifts on the
	 * applications response). */
	appliedShifts?: { id: string; start: string; end: string }[];
}

// export interface ApplicationWithJob extends Application {
// 	job: Job;
// }

type ApplicationExpandOptions = {
	// shifts?: Shift[];
	job?: Job;
};

export type ApplicationWithExpansion<T extends Partial<ApplicationExpandOptions> = {}> =
	Application & T;

interface Consultant {
	id: string;
	firstName: string;
	lastName: string;
	profile?: ConsultantProfile;
	hasCustomerExperience?: boolean;
	absences?: Absences[];
}

export interface ConsultantProfile {
	profilePicture?: string;
	documents?: { certificationsByType?: CertificationsByType };
	certifications?: ConsultantCertification[];
	address?: Address;
}

export interface Invitation {
	id: string;
	consultant: Consultant;
	metadata?: { created: string };
	status?: "OPEN" | "DECLINED" | "EXPIRED";
	tb: number;
	tg: number;
}

export enum TaxTypes {
	RUT = "RUT",
	ROT = "ROT",
}

export enum BookingTypes {
	OPEN = "OPEN",
	INVITATION = "INVITATION",
	AUTO = "AUTO",
}

export interface JobsResponse {
	totalCount: number;
	jobs: Job[];
}

export interface JobScheduleItem {
	id: string;
	contactPersons: CustomerUser[]; // id should not be compulsory
	customer: { id: string; name?: string };
	startDate: Date | string;
	endDate: Date | string;
	orderNumber: string;
	shifts: JobShift[];
	status: JOB_STATUS;
	competence: Competence;
	applications?: Pick<Application, "id" | "consultant" | "shifts" | "status">[];
}

export interface FileData {
	url: string;
	name: string;
}

export interface JobScheduleResponse {
	jobSchedules: JobScheduleItem[];
}

/**
 * View model used while editing a job.
 */
export interface ShiftViewModel {
	localId: string;

	/**
	 * The shift to edit.
	 */
	shift: JobShift;

	/**
	 * Id of the consultant that is or *will* be booked for this shift.
	 * This reflects the local state of the shift before changes has been committed.
	 */
	bookedConsultant?: string;

	/**
	 * Id of the consultant that is currently booked for this shift.
	 * This is what the shift was booked with when the shift was fetched
	 */
	currentBookedConsultant?: string;

	/**
	 * Ids of consultants to invite for this shift.
	 */
	invitedConsultants?: string[];

	/**
	 * Ids of consultants who have applied for this shift.
	 */
	appliedConsultants?: string[];

	/**
	 * Id of the consultant to unbook for this shift.
	 */
	consultantToUnbook?: string;

	/**
	 * Absence type for this shift, if any.
	 * Is retrieved from the shifts time report.
	 */
	absence?: AbsenceType;

	/**
	 * Flag set if shift should be removed.
	 */
	remove?: boolean;
}

export type ScheduleTooltipData = {
	booked: number;
	absents: number;
	unbooked: number;
	denied: number;
};
